import React, { useState } from "react";
import { Label, Input } from "@/components/form";

interface YearFieldProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean, message: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  validateOnBlur?: boolean;
  minYear?: number;
  maxYear?: number;
}

const YearField: React.FC<YearFieldProps> = ({
  id = "yearEstablished",
  name = "yearEstablished",
  value,
  onChange,
  onBlur,
  onValidationChange,
  label = "Year Established",
  placeholder = new Date().getFullYear().toString(),
  required = false,
  error = false,
  hint,
  className = "",
  validateOnBlur = true,
  minYear = 1900,
  maxYear = new Date().getFullYear() + 1,
}) => {
  const [localError, setLocalError] = useState<string>("");

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits for year input
    const rawValue = e.target.value;
    const digits = rawValue.replace(/\D/g, "");
    
    // If non-digit characters were entered and removed, create a new input change event
    if (digits !== rawValue) {
      const sanitizedEvent = {
        ...e,
        target: {
          ...e.target,
          name: name,
          value: digits
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(sanitizedEvent);
    } else {
      // Pass the original event if no sanitization was needed
      onChange(e);
    }
    
    // Clear any local error when user is typing
    if (localError) {
      setLocalError("");
    }
  };

  const handleYearBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (validateOnBlur) {
      // Validate the year format and range
      const yearValue = e.target.value;
      let isValid = true;
      let errorMessage = "";
      
      // Check if it's empty but required
      if (!yearValue && required) {
        isValid = false;
        errorMessage = "Year is required";
      } 
      // Check if it's not exactly 4 digits
      else if (yearValue && !/^\d{4}$/.test(yearValue)) {
        isValid = false;
        errorMessage = `Year must be exactly 4 digits. You entered ${yearValue.length} digits.`;
      } 
      // Check if it's outside of the acceptable range
      else if (yearValue) {
        const numericYear = parseInt(yearValue, 10);
        if (numericYear < minYear) {
          isValid = false;
          errorMessage = `Year cannot be earlier than ${minYear}`;
        } else if (numericYear > maxYear) {
          isValid = false;
          errorMessage = `Year cannot be later than ${maxYear}`;
        }
      }
      
      // Set local error state
      setLocalError(isValid ? "" : errorMessage);
      
      // Report validation status to parent component if callback exists
      if (onValidationChange) {
        onValidationChange(isValid, errorMessage);
      }
      
      // Send validation events through onChange to work with the parent component
      if (!isValid) {
        // Report error
        const errorEvent = {
          target: {
            name: name + '_error',
            value: errorMessage
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(errorEvent);
      }
      
      // NOTE: Removed problematic validation events through onChange
      // These were causing form fields to display 'true'/'false' instead of actual values
      // Validation is already handled through the onValidationChange callback above
    }
    
    // Call the provided onBlur if exists
    if (onBlur) {
      onBlur(e);
    }
  };

  // Determine which error message to show
  const displayError = localError || (typeof error === 'string' ? error : hint);

  return (
    <div className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <Input
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleYearChange}
        onBlur={handleYearBlur}
        error={!!error || !!localError}
        hint={displayError}
        required={required}
        inputMode="numeric"
        maxLength={4} // Year is always 4 digits
      />
    </div>
  );
};

export default YearField; 